import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/api/services/authService';
import { PasswordStrength } from '@/components/ui/password-strength';
import { Eye, EyeOff, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import PringLogo from '@/assets/Pring_Logo_black.png';

export const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('pages.resetPassword.errors.noToken'));
      return;
    }

    // Validar que el token existe (opcional, se puede hacer aquí o en el submit)
    setStatus('form');
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError(t('validation.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('validation.passwordTooShort'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.resetPassword({
        token: token as string,
        newPassword: password,
      });
      if (response.success) {
        setStatus('success');
        setMessage(t('pages.resetPassword.success.message'));
      } else {
        setError(response.error.message || t('pages.resetPassword.errors.resetFailed'));
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError(t('pages.resetPassword.errors.resetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-black px-8 pt-8 pb-4 flex flex-col items-center">
            <img src={PringLogo} alt="Pring Logo" className="h-20 w-auto mb-2" />
            <h2 className="text-2xl font-extrabold text-white text-center tracking-tight">
              {t('common.appTitle')}
            </h2>
          </div>
          <div className="bg-white px-8 py-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('pages.resetPassword.errors.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-6">{message}</p>
            <button
              onClick={handleGoToLogin}
              className="w-full py-2 px-4 rounded-md text-white bg-brand-orange hover:bg-orange-600 font-semibold text-base transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-4 h-4" />
              {t('pages.resetPassword.errors.goToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-black px-8 pt-8 pb-4 flex flex-col items-center">
            <img src={PringLogo} alt="Pring Logo" className="h-20 w-auto mb-2" />
            <h2 className="text-2xl font-extrabold text-white text-center tracking-tight">
              {t('common.appTitle')}
            </h2>
          </div>
          <div className="bg-white px-8 py-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('pages.resetPassword.success.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-6">{message}</p>
            <button
              onClick={handleGoToLogin}
              className="w-full py-2 px-4 rounded-md text-white bg-brand-orange hover:bg-orange-600 font-semibold text-base transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-4 h-4" />
              {t('pages.resetPassword.success.goToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="bg-black px-8 pt-8 pb-4 flex flex-col items-center">
          <img src={PringLogo} alt="Pring Logo" className="h-20 w-auto mb-2" />
          <h2 className="text-2xl font-extrabold text-white text-center tracking-tight">
            {t('common.appTitle')}
          </h2>
        </div>
        <div className="bg-white px-8 py-8">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('pages.resetPassword.title')}
            </h3>
            <p className="text-sm text-gray-600">{t('pages.resetPassword.description')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.resetPassword.newPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange text-gray-900 bg-white"
                  placeholder={t('pages.resetPassword.newPasswordPlaceholder')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {password && <PasswordStrength password={password} />}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.resetPassword.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange text-gray-900 bg-white"
                  placeholder={t('pages.resetPassword.confirmPasswordPlaceholder')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded text-center text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !password || !confirmPassword}
              className="w-full py-2 px-4 rounded-md text-white bg-brand-orange hover:bg-orange-600 font-semibold text-base transition-colors duration-200 disabled:opacity-60 mt-4 flex items-center justify-center gap-2"
            >
              {isLoading && (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isLoading
                ? t('pages.resetPassword.resetting')
                : t('pages.resetPassword.resetButton')}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleGoToLogin}
              className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors duration-200"
            >
              {t('pages.resetPassword.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
